"use client";

import { useEffect, useMemo, useState } from "react";

import { strings } from "@perceptron/constants";
import { Button } from "@perceptron/ui/components/button";
import { Sheet, SheetContent, SheetTitle } from "@perceptron/ui/components/sheet";
import { cn } from "@perceptron/utils/cn";

import { getTemplatesByCategory, getTemplatesForMode, TEMPLATE_REGISTRY } from "@assets/templates";
import { useIsMobile } from "@demo/hooks/useIsMobile";
import { useMode } from "@demo/hooks/useMode";

import ExampleCard from "./ExampleCard";
import FeaturedCarousel from "./FeaturedCarousel";
import { useFeaturedTemplates } from "./useFeaturedTemplates";
import { useTemplateSelect } from "./useTemplateSelect";

type ExamplesPanelProps = {
  isVisible: boolean;
  onClose: () => void;
  onTemplateSelect: () => void;
};

const ExamplesPanel = ({ isVisible, onClose, onTemplateSelect }: ExamplesPanelProps) => {
  // Source examples for the active mode so each mode shows only its own; the
  // preview components below are mode-agnostic and stay unchanged.
  const [mode] = useMode();
  const modeTemplates = useMemo(() => getTemplatesForMode(TEMPLATE_REGISTRY.templates, mode), [mode]);
  const featuredTemplates = useFeaturedTemplates(mode);
  const videoTemplates = useMemo(() => getTemplatesByCategory(modeTemplates, "video"), [modeTemplates]);
  const imageTemplates = useMemo(() => getTemplatesByCategory(modeTemplates, "image"), [modeTemplates]);

  // Two-step open: render in the closed state on first paint, then flip to
  // the open state on the next animation frame so the desktop CSS transition
  // runs. (Phone path uses Radix Sheet which manages its own enter animation.)
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setHasMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const isOpen = isVisible && hasMounted;

  const handleSelect = useTemplateSelect(onTemplateSelect);

  const isPhone = useIsMobile();

  const headerTitle = isPhone ? (
    // SheetTitle is required by Radix Dialog for screen readers. Override the
    // default font-semibold to match the design's regular-weight body text.
    <SheetTitle className="font-normal body">{strings.templates.panel.title}</SheetTitle>
  ) : (
    <h2 className="body">{strings.templates.panel.title}</h2>
  );

  const panelContent = (
    <>
      <div className="flex items-center justify-between px-4 pb-2 pt-3 text-foreground-secondary">
        {headerTitle}
        <Button
          aria-label={strings.templates.panel.closeAriaLabel}
          className="legal"
          size="sm"
          type="button"
          variant="ghost"
          onClick={onClose}
        >
          {strings.templates.panel.closeLabel}
        </Button>
      </div>

      <div className="mx-2 mb-2 min-h-0 flex-1 overflow-y-auto rounded-2xl [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <div className="space-y-2">
          {featuredTemplates.length > 0 && <FeaturedCarousel templates={featuredTemplates} onSelect={handleSelect} />}

          {videoTemplates.length > 0 && (
            <section className="rounded-2xl bg-background-alt-2 p-3">
              <h3 className="ml-1 pb-2 text-foreground-secondary label-2">
                {strings.templates.panel.videoSectionTitle}
              </h3>
              <ul className="grid grid-cols-1 gap-2 xl:grid-cols-2">
                {videoTemplates.map((template) => (
                  <li key={template.slug}>
                    <ExampleCard template={template} onSelect={handleSelect} />
                  </li>
                ))}
              </ul>
            </section>
          )}

          {imageTemplates.length > 0 && (
            <section className="rounded-2xl bg-background-alt-2 p-3">
              <h3 className="ml-1 pb-2 text-foreground-secondary label-2">
                {strings.templates.panel.imageSectionTitle}
              </h3>
              <ul className="grid grid-cols-1 gap-2 xl:grid-cols-2">
                {imageTemplates.map((template) => (
                  <li key={template.slug}>
                    <ExampleCard template={template} onSelect={handleSelect} />
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </div>
    </>
  );

  // On phones the sheet must cover the entire viewport, including the page
  // chrome (header, mobile tab switcher). Radix Dialog (via shadcn Sheet)
  // handles the portal, focus trap, ESC, scroll-lock, and slide animation —
  // and its portal escapes the `translate-x-*` containing block created by
  // the prompt-area wrapper.
  if (isPhone) {
    return (
      <Sheet
        open={isVisible}
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
      >
        <SheetContent
          aria-describedby={undefined}
          className={cn(
            // Sheet defaults: `flex flex-col gap-4 shadow-lg border-t bg-background`.
            // Override gap (children manage their own spacing), drop the top border
            // so the rounded corners read clean, swap the generic shadow for the
            // brand drop shadow, cap height, and round only the top corners.
            "max-h-[85%] gap-0 rounded-t-3xl border-t-0 p-0",
            "shadow-[0_12px_20px_0_rgba(79,56,6,0.25)] dark:shadow-[0_12px_20px_0_rgba(0,0,0,0.6)]",
          )}
          showCloseButton={false}
          side="bottom"
        >
          {panelContent}
        </SheetContent>
      </Sheet>
    );
  }

  // sm+: rendered in-place inside the prompt area as a non-modal panel.
  return (
    <div
      // `inert` removes the subtree from focus order and the accessibility tree
      // while it's hidden, so `aria-hidden` is redundant. Pairing both also
      // triggers a Chrome warning when the user has just clicked a descendant
      // (focus still there for one tick) since Chrome ignores `inert` when
      // validating `aria-hidden`.
      className={cn(
        "absolute inset-x-3 bottom-3 z-10 flex max-h-[70%] origin-bottom flex-col overflow-hidden rounded-3xl border border-background-alt bg-background shadow-[0_12px_20px_0_rgba(79,56,6,0.25)] dark:shadow-[0_12px_20px_0_rgba(0,0,0,0.6)]",
        "transition-[opacity,transform] duration-300 ease-out will-change-[opacity,transform]",
        isOpen ? "translate-y-0 scale-100 opacity-100" : "translate-y-3 scale-[0.98] opacity-0",
      )}
      inert={!isVisible}
    >
      {panelContent}
    </div>
  );
};

export default ExamplesPanel;
